import { computed, ref } from 'vue';

/**
 * Runtime product resolution (Phase 2.2). ONE bundle serves both products; which one a client sees is
 * decided at runtime from the JWT `ctype` claim (SHOWCASE → vitrine, FULL → full), not at build time
 * via VITE_PWA_MODE. A localStorage hint (`tacita.product`) survives reload so the cold-boot silent
 * refresh can pick the correct (path-scoped) refresh endpoint before any token exists.
 */
export type Product = 'vitrine' | 'full';

const KEY = 'tacita.product';

function readPersisted(): Product | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'vitrine' || v === 'full' ? v : null;
  } catch {
    return null;
  }
}

const product = ref<Product | null>(readPersisted());

/** Reactive product + `isVitrine` helper for shells/components. */
export function useProduct() {
  return { product, isVitrine: computed(() => product.value === 'vitrine') };
}

export function setProduct(p: Product): void {
  product.value = p;
  try { localStorage.setItem(KEY, p); } catch { /* private mode — hint is best-effort */ }
}

export function clearProduct(): void {
  product.value = null;
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

/** The persisted hint (survives reload); used before a token is available. */
export function persistedProduct(): Product | null {
  return readPersisted();
}

/** Current product from reactive state, falling back to the persisted hint. */
export function currentProduct(): Product | null {
  return product.value ?? readPersisted();
}

export function productFromCtype(ctype?: string | null): Product {
  return ctype === 'SHOWCASE' ? 'vitrine' : 'full';
}

/** Home route of a product (vitrine → card, full → home). */
export function productHome(p: Product | null): { name: string } {
  return { name: p === 'vitrine' ? 'card' : 'home' };
}

/** Decode the `ctype` claim from a JWT access token (no network). Returns null if unreadable. */
export function productFromAccessToken(jwt?: string | null): Product | null {
  if (!jwt) return null;
  try {
    const payload = jwt.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return json.ctype ? productFromCtype(json.ctype) : null;
  } catch {
    return null;
  }
}
